import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", url: "", note: "", category: "" };

const UNCATEGORIZED = "Uncategorized";

function normalizeUrl(u) {
  if (!u) return "";
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

export default function LinksInfo() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client-links");
      setItems(data);
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load links");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user?.organization_id]);

  const openCreate = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (row) => { setForm({ title: row.title || "", url: row.url || "", note: row.note || "", category: row.category || "" }); setEditing(row); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const onSave = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error("Title and URL are required"); return; }
    setBusy(true);
    try {
      if (editing && editing.id) { await api.put(`/client-links/${editing.id}`, form); toast.success("Link updated"); }
      else { await api.post("/client-links", form); toast.success("Link added"); }
      close(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!deleteRow) return;
    try { await api.delete(`/client-links/${deleteRow.id}`); toast.success("Link deleted"); setDeleteRow(null); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const existingCategories = Array.from(
    new Set(items.map((i) => (i.category || "").trim()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const groups = items.reduce((acc, row) => {
    const cat = (row.category || "").trim() || UNCATEGORIZED;
    (acc[cat] = acc[cat] || []).push(row);
    return acc;
  }, {});
  const groupNames = Object.keys(groups).sort((a, b) => {
    if (a === UNCATEGORIZED) return 1;
    if (b === UNCATEGORIZED) return -1;
    return a.localeCompare(b);
  });

  const renderCard = (row) => (
    <div key={row.id} data-testid={`link-card-${row.id}`} className="border border-[#E5E0D5] bg-white p-5 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="w-4 h-4 text-[#F37B2A] shrink-0" strokeWidth={1.5} />
          <h3 className="text-[#0F0F0F] font-medium tracking-tight truncate" title={row.title}>{row.title}</h3>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => openEdit(row)} data-testid={`edit-link-${row.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F]" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={() => setDeleteRow(row)} data-testid={`delete-link-${row.id}`} className="p-1.5 text-[#7A7A7A] hover:text-[#B91C1C]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
      <a
        href={normalizeUrl(row.url)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`link-url-${row.id}`}
        className="inline-flex items-center gap-1 text-sm text-[#F37B2A] hover:text-[#D86A1F] underline decoration-[#F37B2A]/40 hover:decoration-[#D86A1F] break-all"
      >
        {row.url}
        <ExternalLink className="w-3 h-3 shrink-0" strokeWidth={1.5} />
      </a>
      {row.note && <p className="text-sm text-[#3A3A3A] whitespace-pre-wrap" data-testid={`link-note-${row.id}`}>{row.note}</p>}
    </div>
  );

  return (
    <div data-testid="links-info-page">
      <PageHeader
        eyebrow="[16] Links & Info"
        title="Links & Info"
        subtitle={`Handy website links for ${user?.organization_name || "this client"}, each with a short note.`}
        actions={isAdmin ? (
          <Button data-testid="add-link-btn" onClick={openCreate} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase">
            <Plus className="w-3.5 h-3.5 mr-2" /> Add Link
          </Button>
        ) : null}
      />

      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        {loading && <div className="font-mono text-xs text-[#7A7A7A] py-10 text-center">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="border border-[#E5E0D5] bg-white px-4 py-16 text-center" data-testid="links-info-empty">
            <Link2 className="w-8 h-8 mx-auto text-[#C9C2B3] mb-3" strokeWidth={1.25} />
            <div className="font-mono text-xs text-[#7A7A7A] mb-3">No links added yet.</div>
            {isAdmin && <button onClick={openCreate} data-testid="empty-add-link-btn" className="font-mono text-xs uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F]">+ Add your first link</button>}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="space-y-8" data-testid="links-grid">
            {groupNames.map((cat) => (
              <section key={cat} data-testid={`link-group-${cat}`}>
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="font-mono text-xs tracking-[0.2em] uppercase text-[#1A1A1A]">{cat}</h2>
                  <span className="font-mono text-[10px] text-[#9A9A9A]">({groups[cat].length})</span>
                  <div className="flex-1 h-px bg-[#E5E0D5]" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groups[cat].map(renderCard)}
                </div>
              </section>
            ))}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="font-mono text-xs text-[#9A9A9A]">Total: {items.length} {items.length === 1 ? "link" : "links"}</div>
        )}
      </div>

      {/* Create/edit modal */}
      <AlertDialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">{editing && editing.id ? "Edit link" : "Add link"}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">A website link for this client and a short note on what it&apos;s for.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="uppercase-label">Title *</Label>
              <Input data-testid="link-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Firewall admin portal" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" autoFocus />
            </div>
            <div>
              <Label className="uppercase-label">URL *</Label>
              <Input data-testid="link-form-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
            <div>
              <Label className="uppercase-label">Category / Heading</Label>
              <Input
                data-testid="link-form-category"
                list="link-category-options"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Portals, Vendors, Monitoring"
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
              <datalist id="link-category-options">
                {existingCategories.map((c) => <option key={c} value={c} />)}
              </datalist>
              <p className="mt-1 font-mono text-[10px] text-[#9A9A9A]">Links group under this heading. Leave blank for &quot;Uncategorized&quot;.</p>
            </div>
            <div>
              <Label className="uppercase-label">Note</Label>
              <Textarea data-testid="link-form-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} placeholder="What is this link for? Login hints, who uses it, etc." className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="link-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="link-save-btn" disabled={busy || !form.title.trim() || !form.url.trim()} onClick={(e) => { e.preventDefault(); onSave(); }} className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white disabled:opacity-50">{busy ? "Saving…" : (editing && editing.id ? "Save changes" : "Add")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">Delete this link?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A]">&quot;{deleteRow?.title}&quot; will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-link-btn" onClick={onDelete} className="rounded-none bg-[#B91C1C] hover:bg-[#cc2200] text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
